/**
 * Agrupación de líneas y texto para el proveedor. La RPC ya trae el origen;
 * aquí se decide cómo se enseña y qué se copia.
 */

export interface OrderDetailLine {
    id: string;
    productName: string;
    variantLabel: string | null;
    sku: string;
    quantity: number;
    unitPriceCents: number;
    lineTotalCents: number;
    catalogProductId?: number | string | null;
    kind?: 'merch' | 'catalog' | null;
    supplierSlug?: string | null;
    supplierName?: string | null;
    orderContactEmail?: string | null;
    supplierReference?: string | null;
    ean?: string | null;
    unitCostCents?: number | null;
}

export interface OriginGroup {
    key: string;
    kind: 'merch' | 'supplier';
    title: string;
    orderContactEmail: string | null;
    lines: OrderDetailLine[];
    subtotalCents: number;
}

export const MERCH_BLOCK_TITLE = 'Stock propio (merch AiPeces)';
export const SUPPLIER_MANUAL_NOTE = 'Pedido a proveedor: gestión manual';

function isCatalogLine(line: OrderDetailLine): boolean {
    if (line.kind === 'catalog') return true;
    if (line.kind === 'merch') return false;
    return line.catalogProductId != null;
}

/** Merch primero, y cada proveedor en el orden en que aparece. */
export function groupLinesByOrigin(lines: readonly OrderDetailLine[]): OriginGroup[] {
    const merch: OrderDetailLine[] = [];
    const suppliers: OriginGroup[] = [];
    const bySlug = new Map<string, OriginGroup>();

    for (const line of lines) {
        if (!isCatalogLine(line)) {
            merch.push(line);
            continue;
        }
        const key = line.supplierSlug?.trim() || 'proveedor';
        let group = bySlug.get(key);
        if (!group) {
            group = {
                key: `supplier:${key}`,
                kind: 'supplier',
                title: line.supplierName?.trim() || key,
                orderContactEmail: line.orderContactEmail?.trim() || null,
                lines: [],
                subtotalCents: 0,
            };
            bySlug.set(key, group);
            suppliers.push(group);
        }
        group.lines.push(line);
        group.subtotalCents += line.lineTotalCents;
    }

    const groups: OriginGroup[] = [];
    if (merch.length > 0) {
        groups.push({
            key: 'merch:stock',
            kind: 'merch',
            title: MERCH_BLOCK_TITLE,
            orderContactEmail: null,
            lines: merch,
            subtotalCents: merch.reduce((sum, line) => sum + line.lineTotalCents, 0),
        });
    }
    groups.push(...suppliers);
    return groups;
}

/** Cuántas salidas tendría el pedido. El precio del envío sigue siendo uno. */
export function expeditionSummary(groups: readonly OriginGroup[]): string {
    if (groups.length === 0) return '';
    const parts = groups.map((group) => (
        group.kind === 'merch'
            ? '1 paquete de merch'
            : `1 expedición de catálogo (${group.title})`
    ));
    const noun = groups.length === 1 ? 'expedición' : 'expediciones';
    return `Este pedido genera ${groups.length} ${noun}: ${parts.join(' y ')}. El checkout cobra un solo envío.`;
}

/** Coste de mercancía solo si todas las líneas lo traen. Si falta una, el margen mentiría. */
export function orderGoodsCostCents(
    lines: readonly { quantity: number; unitCostCents?: number | null }[],
): number | null {
    if (lines.length === 0) return null;
    let total = 0;
    for (const line of lines) {
        if (line.unitCostCents == null) return null;
        total += line.unitCostCents * line.quantity;
    }
    return total;
}

export interface ClipboardAddress {
    fullName?: string | null;
    line1?: string | null;
    line2?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
}

export interface ClipboardLine {
    supplierReference?: string | null;
    ean?: string | null;
    productName: string;
    variantLabel?: string | null;
    quantity: number;
}

export function shippingZoneLabel(label: string | null | undefined): string {
    const text = label?.trim() ?? '';
    if (!text) return '—';
    return text.replace(/\bPeninsula\b/g, 'Península');
}

function filled(value: string | null | undefined): string | null {
    const text = value?.trim();
    return text ? text : null;
}

export function supplierOrderClipboard(input: {
    orderNumber: string;
    lines: readonly ClipboardLine[];
    address: ClipboardAddress;
    phone: string;
}): string {
    const lineText = input.lines.map((line) => {
        const ref = [filled(line.supplierReference), filled(line.ean)].filter(Boolean).join(' / ');
        const description = filled(line.variantLabel)
            ? `${line.productName}, ${line.variantLabel}`
            : line.productName;
        const head = ref ? `${ref} — ${description}` : description;
        return `${head} — ${line.quantity}`;
    });
    const address = [
        filled(input.address.fullName),
        filled(input.address.line1),
        filled(input.address.line2),
        [filled(input.address.postalCode), filled(input.address.city)].filter(Boolean).join(' ') || null,
        filled(input.address.country),
    ].filter((line): line is string => Boolean(line));

    return [
        `Pedido ${input.orderNumber}`,
        '',
        ...lineText,
        '',
        'Envío',
        ...address,
        `Teléfono: ${input.phone.trim()}`,
    ].join('\n');
}
